<template>
  <div class="content">
    <div
        class="editor"
        ref="divRef"
        :contenteditable="!disabled"
        @keyup="handleKeyUp"
        @keydown="handleKeyDown"
        :placeholder="!disabled ? '输入@可选择已有指标' : ''"
    ></div>
    <AtDialog
        v-if="showDialog"
        :visible="showDialog"
        :position="position"
        :queryString="queryString"
        :id="id"
        @onPick="handlePick"
        @onHide="handleHide"
        @onShow="handleShow"
        :selectData="selectData"
    ></AtDialog>
  </div>
</template>
<script setup>
import {
  nextTick,
  reactive,
  ref,
  watch,
  onMounted
} from "vue";
import AtDialog from '../atDialog.vue';

let emit = defineEmits(['changeText'])
const props = defineProps(['value', 'disabled', 'id', 'option']);
const node = ref(null); // 获取到节点
const selIndicator = ref(null); // 选中项的内容
const endIndex = ref(''); // 光标最后停留位置
const queryString = ref(''); // 搜索值
const showDialog = ref(false); // 是否显示弹窗
const divRef = ref(null);
// 弹窗显示位置
let position = reactive({
  x: 0,
  y: 0
});
const selectData = computed(() => {
  return [
    ...(props.option?.prop?.metrics || []),
    ...(props.option?.prop?.dimensions || []),
  ]
})

// 获取光标位置
function getCursorIndex() {
  const selection = window.getSelection();
  return selection.focusOffset; // 选择开始处 focusNode 的偏移量
}

// 获取节点
function getRangeNode() {
  const selection = window.getSelection();
  return selection.focusNode; // 选择的结束节点
}

// 弹窗出现的位置
function getRangeRect() {
  const selection = window.getSelection();
  const range = selection.getRangeAt(0); // 是用于管理选择范围的通用对象
  const rect = range.getClientRects()[0]; // 择一些文本并将获得所选文本的范围
  return {
    x: rect.x,
    y: rect.y
  };
}

// 是否展示 @
function showAt() {
  let rangeNode = getRangeNode();
  if (!rangeNode || rangeNode.nodeType !== Node.TEXT_NODE) return false;
  let content = rangeNode.textContent || '';
  let regx = /@([^@\s]*)$/;
  let match = regx.exec(content.slice(0, getCursorIndex()));
  return match && match.length === 2;
}

// 获取 @ 指标
function getAtIndicator() {
  const content = getRangeNode().textContent || '';
  const regx = /@([^@\s]*)$/;
  const match = regx.exec(content.slice(0, getCursorIndex()));
  if (match && match.length === 2) {
    return match[1];
  }
  return undefined;
}

// 创建标签
function createAtButton(indicator) {
  const btn = document.createElement('span');
  btn.style.display = 'inline-block';
  btn.dataset.indicator = JSON.stringify(indicator);
  btn.className = 'indicator-formula-at-button';
  btn.contentEditable = 'false';
  btn.textContent = `@${indicator.name}`;
  const wrapper = document.createElement('span');
  wrapper.style.display = 'inline-block';
  wrapper.contentEditable = 'false';
  const spaceElem = document.createElement('span');
  spaceElem.style.whiteSpace = 'pre';
  spaceElem.textContent = '\u200b';
  spaceElem.contentEditable = 'false';
  const clonedSpaceElem = spaceElem.cloneNode(true);
  wrapper.appendChild(spaceElem);
  wrapper.appendChild(btn);
  wrapper.appendChild(clonedSpaceElem);
  return wrapper;
}

function replaceString(raw, replacer) {
  return raw.replace(/@([^@\s]*)$/, replacer);
}

// 插入@标签
function replaceAt(sel) {
  const curNode = node.value;
  if (curNode && sel) {
    const content = curNode.textContent || '';
    const preSlice = replaceString(content.slice(0, endIndex.value), '');
    const restSlice = content.slice(endIndex.value);
    const parentNode = curNode.parentNode;
    const nextNode = curNode.nextSibling;
    const previousTextNode = new Text(preSlice);
    const nextTextNode = new Text('\u200b' + restSlice); // 添加 0 宽字符
    const atButton = createAtButton(sel);
    parentNode.removeChild(curNode);
    // 插在文本框中
    if (nextNode) {
      parentNode.insertBefore(previousTextNode, nextNode);
      parentNode.insertBefore(atButton, nextNode);
      parentNode.insertBefore(nextTextNode, nextNode);
    } else {
      parentNode.appendChild(previousTextNode);
      parentNode.appendChild(atButton);
      parentNode.appendChild(nextTextNode);
    }
    // 重置光标的位置
    const range = new Range();
    const selection = window.getSelection();
    range.setStart(nextTextNode, 0);
    range.setEnd(nextTextNode, 0);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

// 键盘抬起事件
function handleKeyUp() {
  if (showAt()) {
    node.value = getRangeNode();
    endIndex.value = getCursorIndex();
    position = getRangeRect();
    queryString.value = getAtIndicator() || '';
    showDialog.value = true;
  } else {
    showDialog.value = false;
    emit('changeText', getInnerHTML())
  }
}

// 键盘按下事件
function handleKeyDown(e) {
  if (showDialog.value) {
    if (e.code === 'ArrowUp' || e.code === 'ArrowDown' || e.code === 'Enter') {
      e.preventDefault();
    }
  }
}

// 插入标签后隐藏选择框
function handlePick(sel) {
  replaceAt(sel);
  selIndicator.value = sel;
  showDialog.value = false;
  emit('changeText', getInnerHTML())
}

// 隐藏选择框
function handleHide() {
  showDialog.value = false;
}

// 显示选择框
function handleShow() {
  showDialog.value = true;
}

function getAllUseIndicators() {
  let childs = divRef.value.children;
  let indicators = [];
  for (let i = 0; i < childs.length; i++) {
    let child = childs[i];
    for (let j = 0; j < child.children.length; j++) {
      let indi = child.children[j];
      if (indi.className === 'indicator-formula-at-button') {
        let dataSet = JSON.parse(indi.dataset.indicator);
        if (!indicators.find(el => el === dataSet.code)) {
          indicators.push(dataSet.code);
        }
      }
    }
  }
  return indicators;
}

function getInnerHTML() {
  return divRef.value.innerHTML;
}

onMounted(() => {
  divRef.value.innerHTML = props.value;
});

defineExpose({
  getAllUseIndicators,
  getInnerHTML
});
</script>

<style scoped lang="scss">
.content {
  width: 100%;
  font-family: sans-serif;
  margin: 10px 0;

  h1 {
    text-align: center;
  }

  .editor {
    margin: 0 auto;
    width: 100%;
    min-height: 100px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 5px;
    text-align: left;
    padding: 0 10px;
    overflow: auto;
    line-height: 1.5;

    &:focus {
      outline: none;
    }

    color: #333;
  }

  .editor:empty::before {
    content: attr(placeholder);
    color: #999;
  }
}
</style>
<style>
.indicator-formula-at-button {
  color: #3e7aff;
  padding: 0 5px;
}
</style>
